/**
 * Slash-command registry shared by the chat-footer input and its autocomplete
 * dropdown. Two kinds of commands:
 *
 *   "mode"      — flips a major feature flag (Deep / Plan). The host owns the
 *                  durable mode state after the prefix is stripped.
 *   "reasoning" — apply an admin-configured model+effort preset for the
 *                  session (see reasoning_presets). The model selector pill
 *                  reflects the resulting locks.
 */
import type { LucideIcon } from "@/components/icons"

import { Brain, Gauge, NotebookPen, Sparkles, Zap } from "@/components/icons"

export type SlashCommandName = "deep" | "plan" | "auto" | "fast" | "thinking" | "bigbrain"

export interface SlashCommand {
  name: SlashCommandName
  aliases?: SlashCommandName[]
  kind: "mode" | "reasoning"
  labelKey: string
  descriptionKey: string
  badgeKey?: string
  icon: LucideIcon
}

export const SLASH_COMMANDS: SlashCommand[] = [
  {
    name: "plan",
    aliases: ["deep"],
    kind: "mode",
    labelKey: "questioninput.deep_mode",
    descriptionKey: "deepExplorer.slash_plan_description",
    badgeKey: "deepExplorer.beta_badge",
    icon: NotebookPen,
  },
  {
    name: "auto",
    kind: "reasoning",
    labelKey: "deepExplorer.slash_auto_label",
    descriptionKey: "deepExplorer.slash_auto_description",
    icon: Sparkles,
  },
  {
    name: "fast",
    kind: "reasoning",
    labelKey: "deepExplorer.slash_fast_label",
    descriptionKey: "deepExplorer.slash_fast_description",
    icon: Gauge,
  },
  {
    name: "thinking",
    kind: "reasoning",
    labelKey: "deepExplorer.slash_thinking_label",
    descriptionKey: "deepExplorer.slash_thinking_description",
    icon: Brain,
  },
  {
    name: "bigbrain",
    kind: "reasoning",
    labelKey: "deepExplorer.slash_bigbrain_label",
    descriptionKey: "deepExplorer.slash_bigbrain_description",
    icon: Zap,
  },
]

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

const COMMAND_NAMES = SLASH_COMMANDS.flatMap((command) => [
  command.name,
  ...(command.aliases ?? []),
])
const NAME_UNION = COMMAND_NAMES.map(escapeRegExp).join("|")

/**
 * Matches the committed form: `/<name><whitespace><rest>`. We use this to
 * strip a confirmed slash command from the input once the user has typed past
 * the name (i.e. once they've started typing the actual prompt).
 */
export const SLASH_COMMAND_COMMITTED_REGEX = new RegExp(`^\\s*\\/(${NAME_UNION})(\\s+)(.*)$`, "is")

/**
 * Matches while the user is still typing the command name: `/foo` with no
 * trailing whitespace. Capture group 1 is the partial (or full) name so the
 * autocomplete can filter against it.
 */
export const SLASH_COMMAND_PARTIAL_REGEX = /^\s*\/([A-Za-z]*)$/

export function matchesPartial(name: string, query: string): boolean {
  if (!query) return true
  return name.toLowerCase().startsWith(query.toLowerCase())
}

export function commandMatchesName(command: SlashCommand, name: SlashCommandName): boolean {
  return command.name === name || command.aliases?.includes(name) === true
}

export function findSlashCommand(name: SlashCommandName): SlashCommand | undefined {
  return SLASH_COMMANDS.find((command) => commandMatchesName(command, name))
}
